import { Resource } from '@nextforce/api'
import { useHeaderHeight } from '@react-navigation/elements'
import { CheckBox, Input, makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { FileFolderDetailsView } from 'file_folder_repository/views/file_folder_details_bottom_sheet'
import { useCallback, useState } from 'react'
import { View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { KeyboardAvoidingView } from 'react-native-keyboard-controller'
import { RoundedButton } from 'shared/components/rounded-button'
import { UserAvatar } from 'shared/components/user/user_avatar'

interface Props {
  resource: Resource | undefined
  onPressThumbnail: () => void
  onSubmit: () => Promise<void>
}

export function AcknowledgeResourceView({
  resource,
  onPressThumbnail,
  onSubmit,
}: Props) {
  const styles = useStyles()
  const { theme } = useTheme()
  const [statementChecked, setStatementChecked] = useState(false)
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const headerHeight = useHeaderHeight()

  const onPressSubmit = useCallback(async () => {
    setSubmitting(true)
    await onSubmit()
    setSubmitting(false)
  }, [onSubmit])

  if (resource?.type !== 'file' || !resource.resourceAcknowledgements) {
    return (
      <View style={styles.errorContainer}>
        <Text>{t('resourceHasNoAckRequests')}</Text>
      </View>
    )
  }

  const acknowledgement = resource.resourceAcknowledgements[0]
  const requester = acknowledgement.requester
  const requireSign = acknowledgement.requireSign

  const canSubmit = statementChecked && (requireSign ? name.length > 0 : true)

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={'padding'}
      keyboardVerticalOffset={headerHeight}
    >
      <ScrollView
        contentContainerStyle={styles.scrollViewContainer}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.fileFolderDetailsContainer}>
          <View style={styles.acknowledgementDetailsContainer}>
            <View style={styles.senderRowContainer}>
              <Text h4 style={styles.senderLabel}>
                {t('from')}
              </Text>
              <UserAvatar user={requester} size={30} />
              <Text
                style={styles.senderName}
              >{`${requester?.firstName} ${requester?.lastName}`}</Text>
            </View>
          </View>
          <Text style={styles.acknowledgementRequirementLabel}>
            {`${requireSign ? t('requiresSign') : t('requiresAck')} :`}
          </Text>
          <View>
            <FileFolderDetailsView
              itemId={resource?.id}
              itemType={resource?.type}
              onPressThumbnail={onPressThumbnail}
              showActions={false}
              showPath={false}
              showOverlay
              showMetadata={false}
            />
          </View>
          <View style={styles.ackInstructionsContainer}>
            <Text style={styles.ackInstructionsText}>
              {requireSign
                ? t('requireSignInstructions')
                : t('requireAckInstructions')}
            </Text>
          </View>
          <View style={styles.statementContainer}>
            <CheckBox
              title={acknowledgement.statement}
              checked={statementChecked}
              textStyle={styles.statementText}
              checkedColor={theme.colors.primary}
              wrapperStyle={styles.statementTextWrapper}
              onPress={() => setStatementChecked(!statementChecked)}
            />
          </View>
        </View>
      </ScrollView>
      {requireSign ? (
        <View style={styles.nameInputContainer}>
          <Input
            placeholder={t('typeNameHere')}
            value={name}
            onChangeText={setName}
            containerStyle={styles.signatureNameInputContainer}
            cursorColor={theme.colors?.primary}
            selectionColor={theme.colors?.primary}
          />
        </View>
      ) : null}
      <RoundedButton
        title={requireSign ? t('sign') : t('acknowledge')}
        onPress={onPressSubmit}
        containerStyle={styles.roundedButtonContainer}
        danger
        disabled={!canSubmit}
        loading={submitting}
      />
    </KeyboardAvoidingView>
  )
}
const useStyles = makeStyles(theme => ({
  container: { flex: 1 },
  fileFolderDetailsContainer: {
    marginTop: 10,
    flex: 1,
  },
  statementText: {
    textAlign: 'justify',
    marginTop: -5,
  },
  signatureNameInputContainer: {
    borderRadius: 10,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors.grey10 : theme.colors.grey6,
    padding: 10,
  },
  errorContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  scrollViewContainer: { flexGrow: 1 },
  senderRowContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 15,
  },
  acknowledgementDetailsContainer: { paddingBottom: 5, marginHorizontal: 15 },
  senderLabel: { marginRight: 10 },
  senderName: { marginLeft: 5, fontSize: 16 },
  acknowledgementRequirementLabel: {
    marginHorizontal: 15,
    marginBottom: 20,
    fontSize: 18,
    fontWeight: 'bold',
  },
  ackInstructionsContainer: {
    marginHorizontal: 25,
    marginVertical: 20,
    flex: 1,
  },
  roundedButtonContainer: { margin: 20 },
  ackInstructionsText: { textAlign: 'justify', fontStyle: 'italic' },
  statementContainer: { marginRight: 15, marginBottom: 20 },
  statementTextWrapper: {
    alignItems: 'flex-start',
  },
  nameInputContainer: {
    marginHorizontal: 15,
  },
}))
